/**
 * Sezione "Destinatari notifiche" nel dettaglio prodotto (E14.3).
 *
 * Mostra la lista dei destinatari ownership (email, ruolo, data aggiunta) e,
 * solo per chi ha PORTFOLIO_MANAGEMENT, un form per aggiungerne uno nuovo e un
 * pulsante "Rimuovi" per ogni riga. Gli utenti con sola VIEW_PORTFOLIO vedono
 * la lista in sola lettura (pattern coerente con ReleaseSection/MappingSection).
 */

import { useState, type FormEvent } from 'react';
import { usePermissions } from '../../auth/index.js';
import { useOwnership, useAddOwnership, useRemoveOwnership } from './hooks.js';
import type { OwnershipRole } from './types.js';
import styles from './Products.module.css';

/** Etichette leggibili per i ruoli ownership (E14.3.2). */
const ETICHETTE_RUOLO: Record<OwnershipRole, string> = {
  responsabile: 'Responsabile',
  distribuzione: 'Distribuzione',
};

/** Form per aggiungere un destinatario ownership. */
function NuovoDestinatarioForm({ productId }: { productId: string }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OwnershipRole>('responsabile');
  const { mutate, isPending, isError, error } = useAddOwnership(productId);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutate(
      { email: email.trim(), role },
      {
        onSuccess: () => {
          setEmail('');
          setRole('responsabile');
        },
      },
    );
  }

  // 409 duplicato → messaggio esplicito; altrimenti messaggio generico dell'errore (es. 400 validazione)
  function messaggioErrore(): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return "Errore durante l'aggiunta del destinatario.";
  }

  return (
    <div className={styles.formCard} data-testid="form-aggiungi-destinatario">
      <p className={styles.formTitle}>Aggiungi destinatario</p>
      <form onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          <div className={styles.formGroup} style={{ flex: 3 }}>
            <label className={styles.formLabel} htmlFor="ownership-email">
              Email *
            </label>
            <input
              id="ownership-email"
              type="email"
              className={styles.formInput}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="es. responsabile@example.com"
              data-testid="input-destinatario-email"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="ownership-role">
              Ruolo *
            </label>
            <select
              id="ownership-role"
              className={styles.formSelect}
              value={role}
              onChange={(e) => setRole(e.target.value as OwnershipRole)}
              data-testid="select-destinatario-role"
            >
              <option value="responsabile">Responsabile</option>
              <option value="distribuzione">Distribuzione</option>
            </select>
          </div>
        </div>
        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={isPending}
            data-testid="btn-aggiungi-destinatario"
          >
            {isPending ? 'Aggiunta…' : 'Aggiungi'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="errore-aggiungi-destinatario">
              {messaggioErrore()}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/** Sezione "Destinatari notifiche" del dettaglio prodotto. */
export function OwnershipSection({ productId }: { productId: string }) {
  const { can } = usePermissions();
  const { data: destinatari, isLoading, isError, error } = useOwnership(productId);
  const removeOwnership = useRemoveOwnership(productId);

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  return (
    <div data-testid="sezione-ownership">
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-destinatari">
          Caricamento destinatari…
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-destinatari">
          {error instanceof Error ? error.message : 'Errore nel caricamento dei destinatari'}
        </p>
      )}

      {!isLoading && !isError && destinatari !== undefined && destinatari.length === 0 && (
        <div className={styles.emptyState} data-testid="destinatari-vuoti">
          Nessun destinatario — le notifiche useranno il fallback di piattaforma.
        </div>
      )}

      {!isLoading && !isError && destinatari !== undefined && destinatari.length > 0 && (
        <table className={styles.table} data-testid="tabella-destinatari">
          <thead>
            <tr>
              <th>Email</th>
              <th>Ruolo</th>
              <th>Aggiunto il</th>
              {puoGestire && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {destinatari.map((d) => (
              <tr key={d.id} data-testid={`destinatario-row-${d.id}`}>
                <td>{d.email}</td>
                <td>
                  <span className={styles.badge}>{ETICHETTE_RUOLO[d.role]}</span>
                </td>
                <td>{d.createdAt}</td>
                {puoGestire && (
                  <td>
                    <button
                      className={`${styles.btnDanger} ${styles.btnSmall}`}
                      onClick={() => removeOwnership.mutate(d.id)}
                      disabled={removeOwnership.isPending}
                      data-testid={`btn-rimuovi-destinatario-${d.id}`}
                    >
                      Rimuovi
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {removeOwnership.isError && (
        <span className={styles.inlineMutError} data-testid="errore-rimuovi-destinatario">
          {removeOwnership.error instanceof Error
            ? removeOwnership.error.message
            : 'Errore durante la rimozione del destinatario.'}
        </span>
      )}

      {/* Form aggiunta destinatario (solo con PORTFOLIO_MANAGEMENT) */}
      {puoGestire && <NuovoDestinatarioForm productId={productId} />}
    </div>
  );
}
